import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  DownloadOutlined,
  GroupsOutlined,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  LinkOutlined,
  LocationOnOutlined,
  PlayCircleOutline,
  SchoolOutlined,
  SearchOutlined,
  TuneOutlined,
  VisibilityOutlined,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import ConfirmDialog from "components/atoms/ConfirmDialog/ConfirmDialog";
import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import SelectField from "components/atoms/SelectField/SelectField";
import {
  cancelarInscricaoTurma,
  findMinhasInscricoes,
  findResumoMinhasInscricoes,
} from "API/apiAcoes";
import { useComplementoAcao } from "components/pages/AcoesQualificacao/hooks/useComplementoAcao";
import {
  formatarCpf,
  formatarDataDbParaBr,
  formatarDatetimeCompletoBr,
} from "utils/Masks";
import {
  formatarHoraComprovante,
  salvarPdfComprovanteInscricao,
} from "components/pages/InscricaoAcao/utils/comprovanteInscricao";
import { montarEnderecoTurma } from "utils/turmaEndereco";
import { useAuth } from "resources/AuthProvider";
import stylesButton from "styles/Button.module.css";
import stylesGrid from "styles/Grid.module.css";
import stylesInput from "styles/Input.module.css";
import stylesManagement from "styles/ManagementList.module.css";
import styles from "./GerenciarInscricao.module.css";

type Filters = {
  searchNome: string;
  searchTipoAcao: number | null;
  searchModalidade: number | null;
  tema: number | null;
  searchPeriodoInicio: string;
  searchPeriodoFim: string;
};
type QuickStatus =
  | ""
  | "inscricoes_abertas"
  | "aguardando_inicio"
  | "em_andamento"
  | "encerradas"
  | "aprovadas"
  | "reprovadas";
type Order =
  | "mais_recentes"
  | "mais_antigas"
  | "inicio_turmas"
  | "fim_inscricoes";
type Inscricao = {
  id: number;
  turmaId: number;
  nomeAcao: string;
  nomeTurma: string;
  dataInicio: string;
  dataFim: string;
  horaInicio: string | null;
  horaFim: string | null;
  modalidade: string;
  endereco: string | null;
  cargaHoraria: number | null;
  observacoes: string | null;
  inicioInscricao: string;
  fimInscricao: string;
  horaInicioInscricao: string | null;
  horaFimInscricao: string | null;
  link: string | null;
  resultado: string | null;
  status: string | null;
  dataHoraInscricao: string;
  certificado: {
    dataInicioDisponibilizacao: string | null;
    horaInicioDisponibilizacao: string | null;
    dataFimDisponibilizacao: string | null;
    horaFimDisponibilizacao: string | null;
  } | null;
};

const initialFilters: Filters = {
  searchNome: "",
  searchTipoAcao: null,
  searchModalidade: null,
  tema: null,
  searchPeriodoInicio: "",
  searchPeriodoFim: "",
};
const emptySummary = {
  inscricoes_ativas: 0,
  em_andamento: 0,
  concluidas: 0,
  certificados_disponiveis: 0,
};
const quickStatuses: Array<{
  value: QuickStatus;
  label: string;
  color: string;
}> = [
  { value: "", label: "Todas", color: styles.all },
  {
    value: "inscricoes_abertas",
    label: "Inscrições abertas",
    color: styles.open,
  },
  {
    value: "aguardando_inicio",
    label: "Aguardando início",
    color: styles.waiting,
  },
  { value: "em_andamento", label: "Em andamento", color: styles.progress },
  { value: "encerradas", label: "Encerradas", color: styles.closed },
  { value: "aprovadas", label: "Aprovadas", color: styles.approved },
  { value: "reprovadas", label: "Reprovadas", color: styles.rejected },
];
const orders: Array<{ value: Order; label: string }> = [
  { value: "mais_recentes", label: "Mais recentes" },
  { value: "mais_antigas", label: "Mais antigas" },
  { value: "inicio_turmas", label: "Início das turmas" },
  { value: "fim_inscricoes", label: "Fim das inscrições" },
];

const toDate = (date?: string | null, time?: string | null) =>
  date ? new Date(`${date}T${time?.slice(0, 5) || "00:00"}:00`) : null;
const normalize = (source: any): Inscricao => {
  const turma = source?.turma ?? source;
  const acao = source?.acao ?? {};
  return {
    id: Number(source?.id ?? 0),
    turmaId: Number(turma?.id ?? source?.turma_id ?? 0),
    nomeAcao: String(acao?.nome ?? source?.acao_nome ?? "Ação de Qualificação"),
    nomeTurma: String(turma?.nome ?? "Turma"),
    dataInicio: String(turma?.data_inicio ?? ""),
    dataFim: String(turma?.data_fim ?? ""),
    horaInicio: turma?.hora_inicio ?? null,
    horaFim: turma?.hora_fim ?? null,
    modalidade: String(turma?.descricao_modalidade ?? turma?.modalidade ?? "-"),
    endereco: montarEnderecoTurma(turma) || null,
    cargaHoraria: turma?.carga_horaria ?? null,
    observacoes: turma?.observacoes ?? null,
    inicioInscricao: String(turma?.data_inicio_inscricao ?? ""),
    fimInscricao: String(turma?.data_termino_inscricao ?? ""),
    horaInicioInscricao: turma?.hora_inicio_inscricao ?? null,
    horaFimInscricao: turma?.hora_termino_inscricao ?? null,
    link: turma?.link ?? null,
    resultado: source?.resultado?.status ?? null,
    status: turma?.status ?? source?.status ?? null,
    dataHoraInscricao: String(source?.inscrito_em ?? source?.created_at ?? ""),
    certificado: source?.certificado
      ? {
          dataInicioDisponibilizacao:
            source.certificado.data_inicio_disponibilizacao ?? null,
          horaInicioDisponibilizacao:
            source.certificado.hora_inicio_disponibilizacao ?? null,
          dataFimDisponibilizacao:
            source.certificado.data_fim_disponibilizacao ?? null,
          horaFimDisponibilizacao:
            source.certificado.hora_fim_disponibilizacao ?? null,
        }
      : null,
  };
};
const getTurmaStatus = (item: Inscricao) => {
  const value = item.status?.toLowerCase();
  const hasStartDateTime = Boolean(item.dataInicio && item.horaInicio);
  if (value === "inscricoes_abertas")
    return ["Inscrições abertas", styles.statusOpen, false] as const;
  if (value === "aguardando_inicio")
    return ["Aguardando início", styles.statusWaiting, false] as const;
  if (value === "em_andamento" && hasStartDateTime)
    return ["Em andamento", styles.statusProgress, false] as const;
  if (value === "em_andamento") return [null, null, false] as const;
  if (["encerrada", "concluida", "cancelada"].includes(value || ""))
    return ["Encerrada", styles.statusClosed, true] as const;
  const now = new Date();
  const start = toDate(item.dataInicio, item.horaInicio);
  const end = toDate(item.dataFim, item.horaFim);
  const registerStart = toDate(item.inicioInscricao, item.horaInicioInscricao);
  const registerEnd = toDate(item.fimInscricao, item.horaFimInscricao);
  if (
    registerStart &&
    registerEnd &&
    now >= registerStart &&
    now <= registerEnd
  )
    return ["Inscrições abertas", styles.statusOpen, false] as const;
  if (start && now < start)
    return ["Aguardando início", styles.statusWaiting, false] as const;
  if (hasStartDateTime && end && now <= end)
    return ["Em andamento", styles.statusProgress, false] as const;
  if (!hasStartDateTime) return [null, null, false] as const;
  return ["Encerrada", styles.statusClosed, true] as const;
};

const toCertificateAvailabilityDate = (
  date?: string | null,
  time?: string | null,
  isEnd = false,
) => {
  if (!date) return null;
  const defaultTime = isEnd ? "23:59:59" : "00:00:00";
  return new Date(`${date}T${time?.slice(0, 8) || defaultTime}`);
};

const isCertificateAvailable = (item: Inscricao) => {
  const certificate = item.certificado;

  if (!certificate?.dataInicioDisponibilizacao) return false;

  const now = new Date();
  const start = toCertificateAvailabilityDate(
    certificate.dataInicioDisponibilizacao,
    certificate.horaInicioDisponibilizacao,
  );
  const end = toCertificateAvailabilityDate(
    certificate.dataFimDisponibilizacao,
    certificate.horaFimDisponibilizacao,
    true,
  );

  return Boolean(start && now >= start && (!end || now <= end));
};

const canManage = (item: Inscricao) => {
  const now = new Date();
  const start = toDate(item.inicioInscricao, item.horaInicioInscricao);
  const end = toDate(item.fimInscricao, item.horaFimInscricao);
  return Boolean(end && (!start || now >= start) && now <= end);
};

const ScreenSkeleton = () => (
  <main className={styles.page} aria-busy="true">
    <header className={styles.pageHeader}>
      <Skeleton width={260} height={42} />
      <Skeleton width={480} />
    </header>
    <section className={styles.summaryGrid}>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className={styles.summaryCard}>
          <Skeleton variant="circular" width={56} height={56} />
          <div>
            <Skeleton width={120} />
            <Skeleton width={60} height={42} />
            <Skeleton width={150} />
          </div>
        </div>
      ))}
    </section>
    <section className={styles.filterPanel}>
      <Skeleton height={42} />
      <div className={styles.skeletonFilters}>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} height={40} />
        ))}
      </div>
    </section>
    <div className={styles.skeletonChips}>
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} width={110} height={36} />
      ))}
    </div>
    <section className={styles.cardsList}>
      {Array.from({ length: 3 }, (_, i) => (
        <article key={i} className={styles.inscriptionCard}>
          <Skeleton width="60%" height={34} />
          <Skeleton height={130} />
          <Skeleton height={40} />
        </article>
      ))}
    </section>
  </main>
);

const GerenciarInscricao = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useMediaQuery("(max-width:767px)");
  const {
    loadingComplementos,
    complementosResolvidos,
    tipologias,
    modalidadesAcao,
    temas,
  } = useComplementoAcao();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<Inscricao[]>([]);
  const [summary, setSummary] = useState(emptySummary);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [applied, setApplied] = useState<Filters>(initialFilters);
  const [status, setStatus] = useState<QuickStatus>("");
  const [order, setOrder] = useState<Order>("mais_recentes");
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [cancelItem, setCancelItem] = useState<Inscricao | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [details, setDetails] = useState<Inscricao | null>(null);

  /*
  useEffect(() => {
    const logViewportWidth = () => {
      console.log("[GerenciarInscricao] Largura da tela:", window.innerWidth, "px");
    };

    logViewportWidth();
    window.addEventListener("resize", logViewportWidth);

    return () => window.removeEventListener("resize", logViewportWidth);
  }, []);
  */

  useEffect(() => setShowFilters(!isMobile), [isMobile]);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const args = [
      applied.searchNome || undefined,
      applied.searchTipoAcao ?? undefined,
      applied.searchModalidade ?? undefined,
      applied.tema ?? undefined,
      applied.searchPeriodoInicio || undefined,
      applied.searchPeriodoFim || undefined,
    ] as const;
    try {
      const [list, totals] = await Promise.all([
        findMinhasInscricoes(
          page,
          perPage,
          ...args,
          status || undefined,
          order,
        ),
        findResumoMinhasInscricoes(...args, status || undefined),
      ]);
      const raw =
        list?.data?.inscricoes ?? list?.inscricoes ?? list?.data ?? [];
      const pagination = list?.data?.pagination ?? list?.pagination;
      setItems(Array.isArray(raw) ? raw.map(normalize) : []);
      setTotal(Number(pagination?.total ?? list?.data?.qtd_inscricoes ?? 0));
      setLastPage(Math.max(1, Number(pagination?.last_page ?? 1)));
      setSummary({ ...emptySummary, ...totals });
    } catch (reason) {
      setItems([]);
      setSummary(emptySummary);
      setTotal(0);
      setLastPage(1);
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível carregar as inscrições.",
      );
    } finally {
      setLoading(false);
    }
  }, [applied, order, page, perPage, status]);
  useEffect(() => {
    void load();
  }, [load]);
  const update = (key: keyof Filters, value: string | number | null) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const apply = () => {
    setPage(1);
    setApplied(filters);
  };
  const clear = () => {
    setFilters(initialFilters);
    setApplied(initialFilters);
    setStatus("");
    setPage(1);
  };
  const confirmCancel = async () => {
    if (!cancelItem) return;
    setCancelLoading(true);
    try {
      const result = await cancelarInscricaoTurma(cancelItem.turmaId);
      if (!result.success) throw new Error(result.message);
      setCancelItem(null);
      await load();
    } catch (reason) {
      setCancelItem(null);
      setError(
        reason instanceof Error
          ? reason.message
          : "Não foi possível cancelar a inscrição.",
      );
    } finally {
      setCancelLoading(false);
    }
  };
  const cardStats = useMemo(
    () => [
      {
        label: "Inscrições Ativas",
        value: summary.inscricoes_ativas,
        text: "Inscrições abertas que podem ser canceladas",
        icon: <DescriptionOutlined className={styles.iconCard} />,
        color: styles.blue,
      },
      {
        label: "Em Andamento",
        value: summary.em_andamento,
        text: "Ações que já iniciaram e estão em andamento",
        icon: <PlayCircleOutline className={styles.iconCard} />,
        color: styles.amber,
      },
      {
        label: "Concluídas",
        value: summary.concluidas,
        text: "Ações encerradas",
        icon: <CheckCircleOutline className={styles.iconCard} />,
        color: styles.green,
      },
      {
        label: "Certificados Disponíveis",
        value: summary.certificados_disponiveis,
        text: "Certificados e declarações disponíveis para emissão",
        icon: <WorkspacePremiumOutlined className={styles.iconCard} />,
        color: styles.purple,
      },
    ],
    [summary],
  );
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  if (loading || loadingComplementos || !complementosResolvidos)
    return <ScreenSkeleton />;
  return (
    <main className={styles.page}>
      <header className={styles.pageHeader}>
        <h1>Minhas Inscrições</h1>
        <p>
          Aqui estão as ações de qualificação nas quais você está inscrito. Você
          pode visualizar os detalhes da turma, imprimir o comprovante ou
          cancelar sua inscrição.
        </p>
      </header>
      <section className={styles.summaryGrid}>
        {cardStats.map((card) => (
          <article key={card.label} className={styles.summaryCard}>
            <div className={`${styles.summaryIcon} ${card.color}`}>
              {card.icon}
            </div>
            <div>
              <strong>{card.label}</strong>
              <b className={card.color}>{card.value.toLocaleString("pt-BR")}</b>
              <span>{card.text}</span>
            </div>
          </article>
        ))}
      </section>
      <section className={styles.filterPanel}>
        <div className={styles.searchRow}>
          <TextField
            fullWidth
            size="small"
            variant="standard"
            placeholder="Pesquisar por nome da ação"
            value={filters.searchNome}
            onChange={(event) => update("searchNome", event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && apply()}
            InputProps={{
              className: stylesInput.inputSoft,
              disableUnderline: true,
              startAdornment: <SearchOutlined className={styles.searchIcon} />,
            }}
          />
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<TuneOutlined />}
            onClick={() => setShowFilters((value) => !value)}
          >
            Filtros e período
          </Button>
        </div>
        {showFilters && (
          <div className={`${stylesGrid.grid12} ${styles.filterFields}`}>
            <div className={stylesGrid.inputGroup3}>
              <SearchSelect
                label=""
                placeholder="Tipo da ação"
                options={tipologias}
                value={filters.searchTipoAcao}
                onChange={(value) => update("searchTipoAcao", value)}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSoft}
              />
            </div>
            <div className={stylesGrid.inputGroup3}>
              <SearchSelect
                label=""
                placeholder="Tema"
                options={temas}
                value={filters.tema}
                onChange={(value) => update("tema", value)}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSoft}
              />
            </div>
            <div className={stylesGrid.inputGroup2}>
              <SearchSelect
                label=""
                placeholder="Modalidade"
                options={modalidadesAcao}
                value={filters.searchModalidade}
                onChange={(value) => update("searchModalidade", value)}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSoft}
              />
            </div>
            <div className={stylesGrid.inputGroup2}>
              <TextField
                type="date"
                size="small"
                fullWidth
                variant="standard"
                value={filters.searchPeriodoInicio}
                onChange={(event) =>
                  update("searchPeriodoInicio", event.target.value)
                }
                InputProps={{
                  className: stylesInput.inputSoft,
                  disableUnderline: true,
                }}
              />
            </div>
            <div className={stylesGrid.inputGroup2}>
              <TextField
                type="date"
                size="small"
                fullWidth
                variant="standard"
                value={filters.searchPeriodoFim}
                onChange={(event) =>
                  update("searchPeriodoFim", event.target.value)
                }
                InputProps={{
                  className: stylesInput.inputSoft,
                  disableUnderline: true,
                }}
              />
            </div>
            <div
              className={`${stylesGrid.inputGroup2} ${styles.filterActions}`}
            >
              <Button
                variant="outlined"
                className={stylesButton.buttonSmallSoftMuted}
                onClick={clear}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                className={stylesButton.buttonSmallSoftPrimary}
                onClick={apply}
              >
                Filtrar
              </Button>
            </div>
          </div>
        )}
      </section>
      {error && <div className={styles.error}>{error}</div>}
      <section className={styles.chips}>
        {quickStatuses.map((chip) => (
          <button
            key={chip.value || "all"}
            type="button"
            className={`${styles.chip} ${chip.color} ${status === chip.value ? styles.selectedChip : ""}`}
            onClick={() => {
              setStatus(chip.value);
              setPage(1);
            }}
          >
            {chip.label}
          </button>
        ))}
      </section>
      <section className={styles.listHeader}>
        <div>
          <strong>
            {total.toLocaleString("pt-BR")} inscrições encontradas
          </strong>
          <span>
            Exibindo {start} a {end} de {total}
          </span>
        </div>
        <div className={styles.order}>
          <label htmlFor="order">Ordenar por</label>
          <TextField
            id="order"
            select
            size="small"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as Order);
              setPage(1);
            }}
          >
            {orders.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
        </div>
      </section>
      <section className={styles.cardsList}>
        {items.length === 0 ? (
          <div className={styles.empty}>Nenhuma inscrição encontrada.</div>
        ) : (
          items.map((item) => (
            <InscricaoCard
              key={item.id || item.turmaId}
              item={item}
              onDetails={setDetails}
              onCancel={setCancelItem}
              onCertificates={() => navigate("/certificados-declaracoes")}
              participantName={user?.nome_social?.trim() || user?.nome || "-"}
              participantCpf={formatarCpf(user?.cpf) || "-"}
            />
          ))
        )}
      </section>
      {items.length > 0 && (
        <div className={stylesManagement.pagination}>
          <div className={styles.paginationSelect}>
            <span>Resultados por página</span>
            <SelectField
              label=""
              value={String(perPage)}
              onChange={(value) => {
                setPerPage(Number(value));
                setPage(1);
              }}
              options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
              inputClassName={stylesInput.inputSoft}
              disableClearable
              disableTyping
            />
          </div>
          <div className={styles.pageControls}>
            <IconButton disabled={page === 1} onClick={() => setPage(1)}>
              <KeyboardDoubleArrowLeftOutlined />
            </IconButton>
            <IconButton
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              <KeyboardArrowLeftOutlined />
            </IconButton>
            <strong>
              Página {page} de {lastPage}
            </strong>
            <IconButton
              disabled={page === lastPage}
              onClick={() => setPage((value) => value + 1)}
            >
              <KeyboardArrowRightOutlined />
            </IconButton>
            <IconButton
              disabled={page === lastPage}
              onClick={() => setPage(lastPage)}
            >
              <KeyboardDoubleArrowRightOutlined />
            </IconButton>
          </div>
          <div className={styles.paginationSelect}>
            <span>Ir para a página</span>
            <SelectField
              label=""
              value={String(page)}
              onChange={(value) => setPage(Number(value))}
              options={Array.from({ length: lastPage }, (_, index) => ({
                nome: String(index + 1),
              }))}
              inputClassName={stylesInput.inputSoft}
              disableTyping
            />
          </div>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(cancelItem)}
        loading={cancelLoading}
        onClose={() => setCancelItem(null)}
        onConfirm={() => void confirmCancel()}
        title="Cancelar inscrição"
        message={`Deseja realmente cancelar sua inscrição na turma "${cancelItem?.nomeTurma || ""}"?`}
        titleButtonConfirm="Cancelar inscrição"
        titleLoadButton="Cancelando..."
      />
      <Dialog
        open={Boolean(details)}
        onClose={() => setDetails(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Detalhes da inscrição</DialogTitle>
        <DialogContent>
          {details && (
            <div className={styles.dialog}>
              <h3>{details.nomeAcao}</h3>
              <p>
                <strong>Turma:</strong> {details.nomeTurma}
              </p>
              <p>
                <strong>Período:</strong>{" "}
                {formatarDataDbParaBr(details.dataInicio)} a{" "}
                {formatarDataDbParaBr(details.dataFim)}
              </p>
              {details.endereco && (
                <p>
                  <strong>Endereço:</strong> {details.endereco}
                </p>
              )}
              {details.link && (
                <p>
                  <strong>Link:</strong>{" "}
                  <a href={details.link} target="_blank" rel="noreferrer">
                    {details.link}
                  </a>
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

const InscricaoCard = ({
  item,
  onDetails,
  onCancel,
  onCertificates,
  participantName,
  participantCpf,
}: {
  item: Inscricao;
  onDetails: (item: Inscricao) => void;
  onCancel: (item: Inscricao) => void;
  onCertificates: () => void;
  participantName: string;
  participantCpf: string;
}) => {
  const [statusLabel, statusClass] = getTurmaStatus(item);
  const result =
    item.resultado?.toUpperCase() ??
    (item.dataInicio && item.horaInicio ? "EM ANDAMENTO" : null);
  const resultClass =
    result === "APROVADO"
      ? styles.resultApproved
      : result === "REPROVADO"
        ? styles.resultRejected
        : styles.resultProgress;
  const manageable = canManage(item);
  const certificateAvailable = isCertificateAvailable(item);
  const isEad = item.modalidade.toLocaleLowerCase("pt-BR").includes("ead");
  const receiptLink = isEad ? item.link?.trim() || null : null;
  const receiptAddress = isEad ? null : item.endereco;

  const left = [
    [<GroupsOutlined key="turma" />, "Turma", item.nomeTurma],
    [
      <CalendarMonthOutlined key="periodo" />,
      "Período",
      `${formatarDataDbParaBr(item.dataInicio)} a ${formatarDataDbParaBr(item.dataFim)}`,
    ],
    [<SchoolOutlined key="modalidade" />, "Modalidade", item.modalidade],
    ...(item.horaInicio && item.horaFim
      ? [
          [
            <AccessTimeOutlined key="horario" />,
            "Horário",
            `${formatarHoraComprovante(item.horaInicio)} às ${formatarHoraComprovante(item.horaFim)}`,
          ],
        ]
      : []),
    ...(item.cargaHoraria !== null
      ? [
          [
            <AccessTimeOutlined key="carga-horaria" />,
            "Carga horária",
            `${item.cargaHoraria} horas`,
          ],
        ]
      : []),
  ];
  return (
    <article className={styles.inscriptionCard}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <GroupsOutlined />
          <div>
            <h2>{item.nomeAcao}</h2>
            <span>{item.nomeTurma}</span>
          </div>
        </div>
        <div className={styles.badges}>
          {statusLabel && statusClass && (
            <span className={`${styles.badge} ${statusClass}`}>
              {statusLabel}
            </span>
          )}
          {result && (
            <span className={`${styles.badge} ${resultClass}`}>
              {result === "APROVADO" && <CheckCircleOutline />}
              {result === "REPROVADO" && <CancelOutlined />}
              {result === "APROVADO"
                ? "Aprovado"
                : result === "REPROVADO"
                  ? "Reprovado"
                  : "Em andamento"}
            </span>
          )}
        </div>
      </header>
      <div className={styles.details}>
        <div>
          {left.map(([icon, label, value], index) => (
            <p key={index}>
              {icon as React.ReactNode}
              <strong>{label}:</strong>
              <span>{value as string}</span>
            </p>
          ))}
        </div>
        <div>
          {item.endereco && !item.modalidade.toLowerCase().includes("ead") && (
            <p>
              <LocationOnOutlined />
              <strong>Endereço:</strong>
              <span>{item.endereco}</span>
            </p>
          )}
          {item.link && (
            <p>
              <LinkOutlined />
              <strong>Link da sala virtual:</strong>
              <a href={item.link} target="_blank" rel="noreferrer">
                {item.link}
              </a>
            </p>
          )}
          {item.observacoes?.trim() && (
            <p>
              <DescriptionOutlined />
              <strong>Observações:</strong>
              <span>{item.observacoes}</span>
            </p>
          )}
        </div>
      </div>
      <footer className={styles.actions}>
        <Button
          variant="outlined"
          className={stylesButton.buttonSoftOutline}
          startIcon={<VisibilityOutlined />}
          onClick={() => onDetails(item)}
        >
          Visualizar detalhes
        </Button>
        {manageable && (
          <Button
            variant="outlined"
            className={`${stylesButton.buttonSoftOutline} ${styles.receiptButton}`}
            startIcon={<DownloadOutlined />}
            onClick={() =>
              salvarPdfComprovanteInscricao({
                nomeInscrito: participantName,
                cpfInscrito: participantCpf,
                dataHoraInscricao:
                  formatarDatetimeCompletoBr(item.dataHoraInscricao) || "-",
                nomeAcao: item.nomeAcao,
                nomeTurma: item.nomeTurma,
                dataInicio: item.dataInicio,
                dataFim: item.dataFim,
                horaInicio: item.horaInicio,
                horaFim: item.horaFim,
                modalidade: item.modalidade,
                cargaHoraria: item.cargaHoraria,
                endereco: receiptAddress,
                link: receiptLink,
                observacoes: item.observacoes,
              })
            }
          >
            Segunda via da inscrição
          </Button>
        )}
        {manageable && (
          <Button
            variant="outlined"
            className={styles.cancel}
            startIcon={<CancelOutlined />}
            onClick={() => onCancel(item)}
          >
            Cancelar inscrição
          </Button>
        )}
        {certificateAvailable && (
          <Button
            variant="contained"
            className={`${stylesButton.buttonSoftPrimary} ${styles.certificateButton}`}
            startIcon={<WorkspacePremiumOutlined />}
            onClick={onCertificates}
          >
            Certificados e Declarações
          </Button>
        )}
      </footer>
    </article>
  );
};
export default GerenciarInscricao;
